import React from "react";
import {
  Box,
  Button,
  CloseButton,
  Group,
  Modal,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { IconDeviceFloppy } from "@tabler/icons-react";
import styles from "./UnsavedChangesDialog.module.css";

export interface UnsavedChangesDialogProps {
  opened: boolean;
  /** Name of the thing with unsaved changes, e.g. a report's display name. */
  name: string;
  onSave: () => void;
  onDiscard: () => void;
  /** Cancel: the X, Esc or a click outside closes the dialog and stays. */
  onKeepEditing: () => void;
  /** True while the save is in flight (disables the actions). */
  saving?: boolean;
  /** Body copy. Defaults to the report-view wording. */
  description?: React.ReactNode;
  saveLabel?: string;
}

const DEFAULT_DESCRIPTION =
  "You changed this view. Save it as the default, or discard your changes.";

/**
 * "Save changes to “{name}”?" — shown when leaving something with unsaved
 * changes. Two actions: Discard changes and Save both complete the pending
 * navigation. Cancelling (the X, Esc or a click outside) keeps editing.
 */
export default function UnsavedChangesDialog({
  opened,
  name,
  onSave,
  onDiscard,
  onKeepEditing,
  saving = false,
  description = DEFAULT_DESCRIPTION,
  saveLabel = "Save",
}: UnsavedChangesDialogProps) {
  // Compound Modal: Modal.Title gives the dialog its accessible name (the
  // content's aria-labelledby points at it), which a plain <Modal> without a
  // `title` prop doesn't.
  return (
    <Modal.Root
      opened={opened}
      onClose={() => {
        if (!saving) onKeepEditing();
      }}
      centered
      size="md"
      radius="lg"
      padding="lg"
      closeOnClickOutside={!saving}
      closeOnEscape={!saving}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Body>
          <Group align="flex-start" gap="md" wrap="nowrap">
            <ThemeIcon
              className={styles.iconTile}
              size="xl"
              radius="md"
              variant="light"
              color="burntOrange"
              aria-hidden
            >
              <IconDeviceFloppy size={19} />
            </ThemeIcon>
            <Box miw={0} flex={1}>
              <Modal.Title className={styles.title}>
                Save changes to “{name}”?
              </Modal.Title>
              <Text className={styles.body} mt="xs">
                {description}
              </Text>
            </Box>
            <CloseButton
              aria-label="Keep editing"
              onClick={onKeepEditing}
              disabled={saving}
            />
          </Group>
          <Group
            className={styles.actions}
            gap="xs"
            mt="lg"
            justify="flex-end"
            wrap="nowrap"
          >
            <Button
              variant="default"
              className={styles.discard}
              onClick={onDiscard}
              disabled={saving}
            >
              Discard changes
            </Button>
            <Button
              color="teal"
              leftSection={<IconDeviceFloppy size={16} aria-hidden />}
              onClick={onSave}
              loading={saving}
            >
              {saveLabel}
            </Button>
          </Group>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}
